// Lordicon animations come from our own build instead of cdn.lordicon.com.
// The CDN fetch was a single attempt with no retry, so one failed request left
// an icon blank for that user (OP-3180). Icons keep their CDN URLs in code and
// in program data; the URL is mapped to the shipped copy here.
//
// All shipped icons live in one lazily loaded chunk (see iconData.ts) whose
// download starts as soon as this module is evaluated, so by the time the
// first <lord-icon> mounts the data is usually already here and no icon ever
// waits on its own request. The CDN is only used for icons we do not ship, and
// as a last resort for shipped icons when the chunk itself cannot be loaded.
const LORDICON_CDN_URL = /^(?:https?:)?\/\/cdn\.lordicon\.com\/([a-z0-9]+)\.json(?:[?#].*)?$/i;
const ATTEMPTS_PER_SOURCE = 2;

type IconDataModule = typeof import("./iconData");

let iconDataModule: Promise<IconDataModule> | undefined;

// A failed chunk load is dropped so the next icon tries the import again.
function loadIconDataModule(): Promise<IconDataModule> {
  if (!iconDataModule) {
    iconDataModule = import("./iconData").catch((error) => {
      iconDataModule = undefined;
      throw error;
    });
  }
  return iconDataModule;
}

// start the chunk download at boot, in parallel with the rest of the app
loadIconDataModule().catch(() => undefined);

export function getLordiconId(src: string): string | undefined {
  return src.match(LORDICON_CDN_URL)?.[1]?.toLowerCase();
}

async function fetchIconJson(url: string) {
  const response = await fetch(url);
  if (!response.ok) {
    throw new Error(`Lordicon ${response.status} for ${url}`);
  }
  return response.json();
}

// One load per icon however many times it is shown; a failed load is
// dropped from the cache so the next render tries again.
const iconDataCache = new Map<string, Promise<any>>();

// Resolves to undefined when every source fails: the element then skips the
// icon instead of raising an unhandled rejection.
export function loadIconData(src: string): Promise<any> {
  const cached = iconDataCache.get(src);
  if (cached) return cached;

  const request = (async () => {
    let lastError: unknown;
    const id = getLordiconId(src);
    if (id) {
      try {
        const data = (await loadIconDataModule()).getLocalIconData(id);
        if (data !== undefined) return data;
      } catch (error) {
        // chunk failed to load or a shipped file is malformed: fall back to the CDN
        lastError = error;
      }
    }
    for (let attempt = 0; attempt < ATTEMPTS_PER_SOURCE; attempt++) {
      try {
        return await fetchIconJson(src);
      } catch (error) {
        lastError = error;
      }
    }
    iconDataCache.delete(src);
    console.warn(`Lordicon failed to load ${src}`, lastError);
    return undefined;
  })();

  iconDataCache.set(src, request);
  return request;
}
